import classNames from 'classnames'
import { ArtistPreview } from 'components/artists/ArtistPreview'
import { EmblaCarouselSlide } from 'components/carousels/EmblaCarousel'
import { useEmblaPagination } from 'contexts'
import { EmblaPaginationProvider } from 'contexts/providers/EmblaPaginationProvider'
import useEmblaCarousel from 'embla-carousel-react'
import useTranslation from 'next-translate/useTranslation'
import { ComponentProps } from 'react'
import { range } from 'utils/arrays'
import { Artist } from 'utils/data'

type Props = {
  featuredArtists: Artist[]
  getArtistPreviewProps: (
    artist: Artist
  ) => ComponentProps<typeof ArtistPreview>
}

export function ArtistsCarousel({
  featuredArtists,
  getArtistPreviewProps,
}: Props) {
  const [carousel, emblaApi] = useEmblaCarousel({
    loop: true,
    direction: 'ltr',
    inViewThreshold: 1,
  })

  return (
    <div ref={carousel} className='w-full overflow-hidden'>
      <div className='flex touch-pan-y'>
        {range(0, 3).map(i => (
          <EmblaCarouselSlide key={i} className='w-full space-y-4 mr-6'>
            {featuredArtists.slice(i, i + 3).map(artist => (
              <ArtistPreview
                key={artist.id}
                size='sm'
                {...getArtistPreviewProps(artist)}
              />
            ))}
          </EmblaCarouselSlide>
        ))}
      </div>

      <EmblaPaginationProvider emblaApi={emblaApi}>
        <Pagination />
      </EmblaPaginationProvider>
    </div>
  )
}

function Pagination() {
  const { t } = useTranslation('common')
  const { scrollSnaps, onPaginate, selectedIndex } = useEmblaPagination()

  return (
    <div className='relative flex w-full h-[3px] space-x-4 mt-5 items-center justify-center bottom-0'>
      {scrollSnaps.map((_, index) => (
        <button
          type='button'
          key={index}
          className={classNames(
            'w-5 h-full rounded-full cursor-pointer transition-colors',
            index === selectedIndex ? 'bg-cool-900' : ' bg-cool-900/10'
          )}
          onClick={() => onPaginate(index)}
        >
          <p className='sr-only'>{t('view_slide_x', { x: index + 1 })}</p>
        </button>
      ))}
    </div>
  )
}
